import i18next from "i18next";
import { Helmet } from "react-helmet";

import { seo } from "../../seo";
import { appDefaultLanguage } from "../../config";

// FE-29: коды локалей для Open Graph и hreflang.
// tj — таджикский, его код по стандарту tg.
const ogLocaleMap = {
  uz: "uz_UZ",
  ru: "ru_RU",
  en: "en_US",
  tj: "tg_TJ",
};

const hreflangMap = {
  uz: "uz",
  ru: "ru",
  en: "en",
  tj: "tg",
};

const SUPPORTED_LANGUAGES = Object.keys(hreflangMap);

// FE-29: словари seo заполнены не для всех языков (tj отсутствует), а обращение
// seo.titles[language][pageKey] на нём падало с TypeError и роняло страницу целиком.
const pickSeoValue = (dictionary, language, pageKey, fallback = "") =>
  dictionary?.[language]?.[pageKey] ?? dictionary?.[appDefaultLanguage]?.[pageKey] ?? fallback;

const SeoTags = ({ pageKey }) => {
  const { language } = i18next;

  const pageTitle = pickSeoValue(seo.titles, language, pageKey);
  const pageMetaDescription = pickSeoValue(seo.metaDescriptions, language, pageKey);
  const pageMetaKeywords = pickSeoValue(seo.metaKeywords, language, pageKey);
  const pageMetaTitle = pickSeoValue(seo.metaTitles, language, pageKey);
  const pageMetaOgObject = pickSeoValue(seo.metaOgObject, language, pageKey, {});

  const canonicalUrl = pageMetaOgObject?.url || "";
  const ogLocale = ogLocaleMap[language] || ogLocaleMap[appDefaultLanguage];

  // hreflang строим от канонического адреса: язык переключается параметром lang
  const buildAlternate = alternateLanguage => {
    if (!canonicalUrl) {
      return null;
    }

    try {
      const url = new URL(canonicalUrl);
      url.searchParams.set("lang", alternateLanguage);

      return url.toString();
    } catch (error) {
      return null;
    }
  };

  return (
    <Helmet htmlAttributes={{ lang: hreflangMap[language] || hreflangMap[appDefaultLanguage] }}>
      {!!pageTitle && <title>{pageTitle}</title>}

      {!!pageMetaDescription && <meta name="description" content={pageMetaDescription} />}
      {!!pageMetaKeywords && <meta name="keywords" content={pageMetaKeywords} />}
      {!!pageMetaTitle && <meta name="title" content={pageMetaTitle} />}
      {!!canonicalUrl && <link rel="canonical" href={canonicalUrl} />}

      {/* FE-29: языковые версии одной страницы связаны между собой */}
      {SUPPORTED_LANGUAGES.map(alternateLanguage => {
        const href = buildAlternate(alternateLanguage);

        return href ? (
          <link key={alternateLanguage} rel="alternate" hrefLang={hreflangMap[alternateLanguage]} href={href} />
        ) : null;
      })}
      {!!canonicalUrl && <link rel="alternate" hrefLang="x-default" href={canonicalUrl} />}

      <meta property="og:locale" content={ogLocale} />
      {!!pageMetaOgObject?.description && <meta property="og:description" content={pageMetaOgObject.description} />}
      {!!pageMetaOgObject?.image && <meta property="og:image" content={pageMetaOgObject.image} />}
      {!!pageMetaOgObject?.site_name && <meta property="og:site_name" content={pageMetaOgObject.site_name} />}
      {!!pageMetaOgObject?.title && <meta property="og:title" content={pageMetaOgObject.title} />}
      {!!pageMetaOgObject?.type && <meta property="og:type" content={pageMetaOgObject.type} />}
      {!!pageMetaOgObject?.url && <meta property="og:url" content={pageMetaOgObject.url} />}

      <meta name="twitter:card" content="summary_large_image" />
      {!!pageMetaOgObject?.title && <meta name="twitter:title" content={pageMetaOgObject.title} />}
      {!!pageMetaDescription && <meta name="twitter:description" content={pageMetaDescription} />}
      {!!pageMetaOgObject?.image && <meta name="twitter:image" content={pageMetaOgObject.image} />}
    </Helmet>
  );
};

export default SeoTags;
